import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const LS_KEY = "inpalmo_cookie_ack";

/**
 * Cancella cookie orfani lasciati da tracker di terze parti che erano attivi
 * in passato e ora sono stati rimossi (es. PostHog: cookie `ph_*`).
 * Se il codice inietta di nuovo un tracker in futuro, questo cleanup diventa
 * un no-op.
 */
function purgeOrphanTrackingCookies() {
  if (typeof document === "undefined") return;
  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    const hostname = window.location.hostname;
    // Domini candidati per l'invalidazione: host esatto + parent domain (per cookie con Domain=.inpalmo.it)
    const parts = hostname.split(".");
    const parentDomain = parts.length > 2 ? "." + parts.slice(-2).join(".") : hostname;
    const domains = [hostname, "." + hostname, parentDomain];
    for (const raw of cookies) {
      const name = raw.split("=")[0].trim();
      if (!name) continue;
      // Match: ph_ / posthog (PostHog) — nessun altro tracker attivo in InPalmo.
      if (/^ph_/i.test(name) || /^posthog/i.test(name)) {
        for (const d of domains) {
          document.cookie = `${name}=; Max-Age=0; path=/; domain=${d}`;
        }
        document.cookie = `${name}=; Max-Age=0; path=/`;
      }
    }
  } catch (_e) { /* noop */ }
}

/**
 * Minimal, GDPR-correct cookie disclosure for InPalmo.
 *
 * We only use *technical* cookies (session token) — no tracking, no analytics
 * via cookies, no marketing. Under art. 122 of the Italian Codice Privacy and
 * the ePrivacy directive, technical cookies do **not** require prior consent:
 * a transparent informative banner is the legally correct approach.
 *
 * The banner is dismissed permanently with one click and the choice is
 * remembered in localStorage. No real "consent" is stored: we just hide the
 * notice.
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Pulisci sempre eventuali cookie di tracker precedenti (es. ph_* di PostHog),
    // a prescindere dallo stato del banner. Non tocchiamo il cookie di sessione.
    purgeOrphanTrackingCookies();
    try {
      const acked = localStorage.getItem(LS_KEY) === "1";
      if (!acked) {
        // Slight delay so the banner doesn't compete with the hero animation
        const t = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(t);
      }
    } catch (_e) { /* localStorage disabled, skip */ }
  }, []);

  const acknowledge = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch (_e) { /* noop */ }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="
            fixed bottom-3 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md
            z-[60] bg-white border border-[#E5E3DB] shadow-xl rounded-2xl
            pb-[max(env(safe-area-inset-bottom),0px)]
          "
          role="dialog"
          aria-live="polite"
          aria-label="Informativa cookie"
        >
          <div className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl bg-[#FDF6E3] text-[#8B6518] flex items-center justify-center flex-shrink-0">
                <Cookie size={17} />
              </div>
              <div className="min-w-0 flex-1">
                <h3
                  className="text-sm font-semibold text-[#1A1C19]"
                  style={{ fontFamily: "Work Sans, sans-serif" }}
                >
                  Solo cookie tecnici
                </h3>
                <p className="mt-1 text-[13px] text-[#4A524C] leading-relaxed">
                  Usiamo unicamente i cookie necessari al funzionamento del sito (es. il token
                  della tua sessione). <strong>Nessun tracking</strong>, nessun cookie pubblicitario.
                </p>
              </div>
              <button
                type="button"
                onClick={acknowledge}
                aria-label="Chiudi avviso"
                data-testid="cookie-banner-close"
                className="text-[#8E918C] hover:text-[#1A1C19] transition-colors -mr-1 -mt-1"
              >
                <X size={16} />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 pl-12 sm:pl-12">
              <Link
                to="/privacy"
                className="text-[12px] text-[#2E4A3B] font-medium hover:underline"
                onClick={acknowledge}
                data-testid="cookie-banner-privacy-link"
              >
                Leggi l&apos;informativa privacy →
              </Link>
              <button
                type="button"
                onClick={acknowledge}
                data-testid="cookie-banner-ok"
                className="px-3.5 py-1.5 rounded-lg bg-[#2E4A3B] hover:bg-[#243A2E] text-white text-[13px] font-medium transition-colors"
              >
                Ho capito
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
